import { Check, Printer } from 'lucide-react';
import { Modal } from '../../../shared/components/Modal';
import { formatBRL, formatDateTime } from '../../../shared/lib/format';
import type { CartItem, PagamentoItem, RegistrarVendaResponse } from '../types';

interface Props {
  open: boolean;
  venda: RegistrarVendaResponse | null;
  itens: CartItem[];
  pagamentos: PagamentoItem[];
  operadorNome: string;
  onClose: () => void;
}

const FORMA_LABEL: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Cartao Debito',
  cartao_credito: 'Cartao Credito',
  vale_refeicao: 'Vale Refeicao',
  outro: 'Outro',
};

export function ReciboModal({
  open,
  venda,
  itens,
  pagamentos,
  operadorNome,
  onClose,
}: Props) {
  if (!venda) return null;

  const imprimir = () => {
    window.print();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Venda registrada"
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={imprimir}>
            <Printer className="h-4 w-4" />
            Imprimir
          </button>
          <button className="btn-primary" onClick={onClose}>
            <Check className="h-4 w-4" />
            Nova venda
          </button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="flex items-center gap-3 rounded-lg bg-emerald-50 p-3 text-emerald-900">
          <Check className="h-5 w-5" />
          <div>
            <p className="font-semibold">Venda #{venda.numero} registrada</p>
            <p className="text-xs opacity-80">{formatDateTime(new Date())}</p>
          </div>
        </div>

        <div className="space-y-1 rounded-lg border border-dashed border-slate-300 p-3 font-mono text-xs">
          <div className="mb-1 text-center">
            <p className="text-sm font-semibold">FastPDV</p>
            <p className="text-[10px] opacity-70">Cupom nao fiscal</p>
          </div>
          <div className="border-t border-dashed border-slate-300 py-1">
            Venda #{venda.numero} • Operador: {operadorNome}
          </div>

          {itens.map((i) => {
            const qtd = i.produto.tipo === 'peso' ? `${i.quantidade.toFixed(3)}kg` : `${i.quantidade}x`;
            const complTotal = i.complementos.reduce((a, c) => a + c.preco_adicional, 0);
            const sub = (i.preco_base + complTotal) * i.quantidade;
            return (
              <div key={i.uid} className="border-t border-dashed border-slate-300 pt-1">
                <div className="flex justify-between gap-2">
                  <span className="truncate">
                    {qtd} {i.produto.nome}
                    {i.tamanho_nome ? ` (${i.tamanho_nome})` : ''}
                  </span>
                  <span>{formatBRL(sub)}</span>
                </div>
                {i.complementos.length > 0 && (
                  <div className="pl-3 text-[10px] opacity-70">
                    + {i.complementos.map((c) => c.nome).join(', ')}
                  </div>
                )}
                {i.observacao && <div className="pl-3 text-[10px] italic">&quot;{i.observacao}&quot;</div>}
              </div>
            );
          })}

          <div className="border-t border-dashed border-slate-300 pt-1">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatBRL(Number(venda.subtotal))}</span>
            </div>
            {Number(venda.desconto) > 0 && (
              <div className="flex justify-between">
                <span>Desconto</span>
                <span>- {formatBRL(Number(venda.desconto))}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-bold">
              <span>TOTAL</span>
              <span>{formatBRL(Number(venda.total))}</span>
            </div>
          </div>

          <div className="border-t border-dashed border-slate-300 pt-1">
            {pagamentos.map((p, i) => (
              <div key={i} className="flex justify-between">
                <span>{FORMA_LABEL[p.forma] ?? p.forma}</span>
                <span>{formatBRL(p.valor)}</span>
              </div>
            ))}
            {Number(venda.troco) > 0 && (
              <div className="flex justify-between font-semibold">
                <span>Troco</span>
                <span>{formatBRL(Number(venda.troco))}</span>
              </div>
            )}
          </div>
          <p className="border-t border-dashed border-slate-300 pt-1 text-center text-[10px] opacity-70">
            Obrigado pela preferencia!
          </p>
        </div>
      </div>
    </Modal>
  );
}
